import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Plus, Check, X, Download, ClockCounterClockwise, PencilSimple, ChartBar, ShoppingCart, Storefront, Wallet, TrendUp, Warning, Folder, Trash, Question, Book } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';

const Procurement = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null);
  const [lineItems, setLineItems] = useState([{ item_name: '', quantity: 1, price: 0, total: 0 }]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [viewMode, setViewMode] = useState(null); // 'details' or 'audit'
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorHistory, setVendorHistory] = useState([]);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editLineItems, setEditLineItems] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Category management state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  // User Guide state
  const [showGuide, setShowGuide] = useState(false);

  const CHART_COLORS = ['#B2560D', '#505631', '#2B3210', '#E5E2D9', '#FBF8EF', '#8B4513', '#228B22', '#4169E1'];
  const STATUS_COLORS = {
    pending: '#F59E0B',
    approved: '#10B981',
    rejected: '#EF4444',
    ordered: '#3B82F6',
    received: '#6366F1'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, vendorsRes, budgetsRes, dashboardRes, categoriesRes] = await Promise.all([
        api.get('/procurement/requests'),
        api.get('/procurement/vendors'),
        api.get('/procurement/budgets'),
        api.get('/procurement/dashboard'),
        api.get('/procurement/categories')
      ]);
      setRequests(requestsRes.data);
      setVendors(vendorsRes.data);
      setBudgets(budgetsRes.data);
      setDashboardStats(dashboardRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get('/procurement/analytics');
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { item_name: '', quantity: 1, price: 0, total: 0 }]);
  };

  const removeLineItem = (index) => {
    const newItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newItems.length > 0 ? newItems : [{ item_name: '', quantity: 1, price: 0, total: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    
    // Calculate total for this line item
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    
    setLineItems(newItems);
  };

  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/procurement/requests', {
        full_name: formData.get('full_name'),
        category: formData.get('category'),
        priority: formData.get('priority'),
        vendor_name: formData.get('vendor_name') || null,
        needed_by: formData.get('needed_by'),
        description: formData.get('description'),
        line_items: lineItems,
        notes: formData.get('notes'),
        requested_by: user.id,
      });
      toast.success('Purchase request created');
      setOpenDialog(null);
      setLineItems([{ item_name: '', quantity: 1, price: 0, total: 0 }]);
      loadData();
    } catch (error) {
      toast.error('Failed to create request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await api.put(`/procurement/requests/${requestId}/approve`, {
        approved_by: user.id,
      });
      toast.success('Request approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await api.put(`/procurement/requests/${requestId}/reject`, {
        approved_by: user.id,
      });
      toast.success('Request rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await api.delete(`/procurement/requests/${requestId}`);
      toast.success('Request deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleMarkOrdered = async (requestId) => {
    try {
      await api.put(`/procurement/requests/${requestId}/mark-ordered`);
      toast.success('Request marked as ordered');
      loadData();
    } catch (error) {
      toast.error('Failed to mark as ordered');
    }
  };

  const handleMarkReceived = async (requestId) => {
    try {
      await api.put(`/procurement/requests/${requestId}/mark-received`);
      toast.success('Request marked as received');
      loadData();
    } catch (error) {
      toast.error('Failed to mark as received');
    }
  };

  // Category Management Functions
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    try {
      await api.post('/procurement/categories', { name: newCategoryName.trim() });
      toast.success('Category created successfully');
      setNewCategoryName('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) return;
    
    try {
      await api.put(`/procurement/categories/${categoryId}`, { name: editCategoryName.trim() });
      toast.success('Category updated successfully');
      setEditingCategory(null);
      setEditCategoryName('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/procurement/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const isAdmin = user.role === 'admin' || user.role === 'super_admin';
  const isLocked = (status) => status !== 'pending';

  // Helper to get category names for dropdowns
  const categoryNames = categories.map(c => c.name);

  const handleExportData = async () => {
    try {
      const response = await api.get('/procurement/export');
      
      // Convert to CSV
      const data = response.data;
      if (data.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');
      
      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `procurement_requests_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleViewAuditLog = async (request) => {
    setSelectedRequest(request);
    setViewMode('audit');
    try {
      const response = await api.get(`/procurement/requests/${request.id}/audit-logs`);
      setAuditLogs(response.data);
      setOpenDialog('view');
    } catch (error) {
      toast.error('Failed to load audit logs');
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setViewMode('details');
    setOpenDialog('view');
  };

  const handleViewVendor = async (vendor) => {
    setSelectedVendor(vendor);
    try {
      const response = await api.get(`/procurement/vendors/${encodeURIComponent(vendor.name)}/requests`);
      setVendorHistory(response.data);
      setOpenDialog('vendor');
    } catch (error) {
      toast.error('Failed to load vendor history');
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'created': 'Created',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'marked_ordered': 'Marked as Ordered',
      'marked_received': 'Marked as Received',
      'deleted': 'Deleted'
    };
    return labels[action] || action;
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/procurement/vendors', {
        name: formData.get('name'),
        category: formData.get('category'),
        contact_person: formData.get('contact_person'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        address: formData.get('address'),
        account_number: formData.get('account_number'),
        account_name: formData.get('account_name'),
        bank_name: formData.get('bank_name'),
        status: formData.get('status'),
        notes: formData.get('notes'),
      });
      toast.success('Vendor created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create vendor');
    }
  };

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/procurement/budgets', {
        category: formData.get('category'),
        allocated_amount: parseFloat(formData.get('allocated_amount')),
        period: formData.get('period'),
      });
      toast.success('Budget created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create budget');
    }
  };

  // Edit request functions
  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setEditLineItems(request.line_items || [{ item_name: '', quantity: 1, price: 0, total: 0 }]);
    setOpenDialog('editRequest');
  };

  const addEditLineItem = () => {
    setEditLineItems([...editLineItems, { item_name: '', quantity: 1, price: 0, total: 0 }]);
  };

  const removeEditLineItem = (index) => {
    const newItems = editLineItems.filter((_, i) => i !== index);
    setEditLineItems(newItems.length > 0 ? newItems : [{ item_name: '', quantity: 1, price: 0, total: 0 }]);
  };

  const updateEditLineItem = (index, field, value) => {
    const newItems = [...editLineItems];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    setEditLineItems(newItems);
  };

  const calculateEditGrandTotal = () => {
    return editLineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const handleUpdateRequest = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.put(`/procurement/requests/${editingRequest.id}`, {
        full_name: formData.get('full_name'),
        category: formData.get('category'),
        priority: formData.get('priority'),
        vendor_name: formData.get('vendor_name') || null,
        needed_by: formData.get('needed_by'),
        description: formData.get('description'),
        line_items: editLineItems,
        notes: formData.get('notes'),
      });
      toast.success('Request updated successfully');
      setOpenDialog(null);
      setEditingRequest(null);
      setEditLineItems([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    }
  };

  const handleViewBudget = (budget) => {
    setSelectedBudget(budget);
    setOpenDialog('budgetDetails');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="procurement-page">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
            Procurement
          </h1>
          <p className="text-base text-[#505631]">Manage purchase requests, vendors, and budgets</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGuide(true)}
            data-testid="user-guide-button"
            className="flex items-center gap-2 px-4 py-2 bg-[#FBF8EF] border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#E5E2D9] transition-colors"
            title="User Guide"
          >
            <Book size={18} />
            <span className="hidden sm:inline">Help</span>
          </button>
          {(isAdmin || user.role === 'manager') && (
            <button
              onClick={handleExportData}
              data-testid="export-button"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Export Report</span>
            </button>
          )}
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-[#FBF8EF] border border-[#E5E2D9] flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-dashboard">
            <ChartBar size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">Dash</span>
          </TabsTrigger>
          <TabsTrigger value="requests" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-requests">
            <ShoppingCart size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Purchase Requests</span>
            <span className="sm:hidden">Requests</span>
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="vendors" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-vendors">
                <Storefront size={16} className="mr-1 sm:mr-2" />
                Vendors
              </TabsTrigger>
              <TabsTrigger value="budgets" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-budgets">
                <Wallet size={16} className="mr-1 sm:mr-2" />
                Budgets
              </TabsTrigger>
              <TabsTrigger value="categories" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-categories">
                <Folder size={16} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Categories</span>
                <span className="sm:hidden">Cats</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="data-[state=active]:bg-white text-xs sm:text-sm" 
                data-testid="tab-analytics"
                onClick={() => !analyticsData && loadAnalytics()}
              >
                <TrendUp size={16} className="mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Analytics</span>
                <span className="sm:hidden">Stats</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Requests</p>
              <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats?.total_requests || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-amber-600">{dashboardStats?.pending_requests || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-600">{dashboardStats?.approved_requests || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Ordered</p>
              <p className="text-3xl font-bold text-blue-600">{dashboardStats?.ordered_requests || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Spent</p>
              <p className="text-2xl font-bold text-[#B2560D]">₦{(dashboardStats?.total_spent || 0).toLocaleString()}</p>
              <p className="text-xs text-[#505631] mt-1">Received items</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Pending Spend</p>
              <p className="text-2xl font-bold text-amber-600">₦{(dashboardStats?.pending_spend || 0).toLocaleString()}</p>
              <p className="text-xs text-[#505631] mt-1">Approved + Ordered</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Vendors & Budgets</p>
              <p className="text-2xl font-bold text-[#2B3210]">{dashboardStats?.total_vendors || 0} / {dashboardStats?.total_budgets || 0}</p>
              <p className="text-xs text-[#505631] mt-1">Active vendors / budgets</p>
            </div>
          </div>

          {/* Overdue Pending Requests Alert */}
          {dashboardStats?.overdue_pending?.length > 0 && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0">
                  <Warning size={24} weight="bold" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                    Attention Required
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                      {dashboardStats.overdue_pending.length}
                    </span>
                  </h3>
                  <p className="text-sm text-red-700 mb-3">
                    The following requests have been pending for more than 3 days and require immediate attention:
                  </p>
                  <div className="space-y-2">
                    {dashboardStats.overdue_pending.slice(0, 5).map((req) => {
                      const daysAgo = Math.floor((new Date() - new Date(req.created_at)) / (1000 * 60 * 60 * 24));
                      const fullRequest = requests.find(r => r.id === req.id) || req;
                      return (
                        <div 
                          key={req.id} 
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 hover:border-red-400 cursor-pointer transition-colors"
                          onClick={() => { setSelectedRequest(fullRequest); setViewMode('details'); setOpenDialog('view'); }}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                              req.priority === 'high' ? 'bg-red-100 text-red-700' :
                              req.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {req.priority || 'N/A'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#2B3210]">
                                {req.description?.substring(0, 50) || 'No description'}...
                              </p>
                              <p className="text-xs text-[#505631]">
                                By {req.requestor_name} • <span className="text-red-600 font-medium">{daysAgo} days ago</span>
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#2B3210]">₦{(req.total_amount || 0).toLocaleString()}</p>
                            <button 
                              className="text-xs text-[#B2560D] hover:underline font-medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                const fullRequest = requests.find(r => r.id === req.id) || req;
                                setSelectedRequest(fullRequest);
                                setViewMode('details');
                                setOpenDialog('view');
                              }}
                            >
                              Review →
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {dashboardStats.overdue_pending.length > 5 && (
                    <button 
                      className="mt-3 text-sm text-red-700 hover:text-red-800 font-medium"
                      onClick={() => {
                        const tabElement = document.querySelector('[data-testid="tab-requests"]');
                        if (tabElement) tabElement.click();
                      }}
                    >
                      View all {dashboardStats.overdue_pending.length} overdue requests →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-6 bg-white rounded-xl border border-[#E5E2D9] p-6">
            <h3 className="text-lg font-medium text-[#2B3210] mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => setOpenDialog('request')}
                className="flex flex-col items-center gap-2 p-4 bg-[#FBF8EF] rounded-xl hover:bg-orange-50 border border-transparent hover:border-[#B2560D] transition-all group"
                data-testid="quick-new-request"
              >
                <div className="w-12 h-12 rounded-full bg-[#B2560D] text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Plus size={24} />
                </div>
                <span className="text-sm font-medium text-[#2B3210]">New Request</span>
                <span className="text-xs text-[#505631]">Create purchase request</span>
              </button>

              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      const tabElement = document.querySelector('[data-testid="tab-requests"]');
                      if (tabElement) tabElement.click();
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-[#FBF8EF] rounded-xl hover:bg-amber-50 border border-transparent hover:border-amber-500 transition-all group"
                    data-testid="quick-pending-approvals"
                  >
                    <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                      <ClockCounterClockwise size={24} />
                    </div>
                    <span className="text-sm font-medium text-[#2B3210]">Pending Approvals</span>
                    <span className="text-xs text-[#505631]">{dashboardStats?.pending_requests || 0} awaiting review</span>
                  </button>

                  <button
                    onClick={() => setOpenDialog('vendor')}
                    className="flex flex-col items-center gap-2 p-4 bg-[#FBF8EF] rounded-xl hover:bg-emerald-50 border border-transparent hover:border-emerald-500 transition-all group"
                    data-testid="quick-add-vendor"
                  >
                    <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Storefront size={24} />
                    </div>
                    <span className="text-sm font-medium text-[#2B3210]">Add Vendor</span>
                    <span className="text-xs text-[#505631]">Register new supplier</span>
                  </button>

                  <button
                    onClick={() => setOpenDialog('budget')}
                    className="flex flex-col items-center gap-2 p-4 bg-[#FBF8EF] rounded-xl hover:bg-blue-50 border border-transparent hover:border-blue-500 transition-all group"
                    data-testid="quick-create-budget"
                  >
                    <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Wallet size={24} />
                    </div>
                    <span className="text-sm font-medium text-[#2B3210]">Create Budget</span>
                    <span className="text-xs text-[#505631]">Set spending limits</span>
                  </button>
                </>
              )}

              {!isAdmin && (
                <button
                  onClick={() => {
                    const tabElement = document.querySelector('[data-testid="tab-requests"]');
                    if (tabElement) tabElement.click();
                  }}
                  className="flex flex-col items-center gap-2 p-4 bg-[#FBF8EF] rounded-xl hover:bg-blue-50 border border-transparent hover:border-blue-500 transition-all group"
                  data-testid="quick-my-requests"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShoppingCart size={24} />
                  </div>
                  <span className="text-sm font-medium text-[#2B3210]">My Requests</span>
                  <span className="text-xs text-[#505631]">View your submissions</span>
                </button>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          {requests.length > 0 && (
            <div className="mt-6 bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-[#2B3210]">Recent Requests</h3>
                <button 
                  onClick={() => {
                    const tabElement = document.querySelector('[data-testid="tab-requests"]');
                    if (tabElement) tabElement.click();
                  }}
                  className="text-sm text-[#B2560D] hover:underline"
                >
                  View all →
                </button>
              </div>
              <div className="space-y-3">
                {requests.slice(0, 5).map((request) => (
                  <div 
                    key={request.id} 
                    className="flex items-center justify-between p-3 bg-[#FBF8EF] rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedRequest(request); setViewMode('details'); }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        request.status === 'pending' ? 'bg-amber-500' :
                        request.status === 'approved' ? 'bg-emerald-500' :
                        request.status === 'rejected' ? 'bg-red-500' :
                        request.status === 'ordered' ? 'bg-blue-500' : 'bg-purple-500'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-[#2B3210]">{request.description?.substring(0, 50) || 'No description'}...</p>
                        <p className="text-xs text-[#505631]">{request.requestor_name} • {request.category || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#2B3210]">₦{(request.total_amount || 0).toLocaleString()}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        request.status === 'ordered' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>{request.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Purchase Requests</h2>
              <Dialog open={openDialog === 'request'} onOpenChange={(open) => setOpenDialog(open ? 'request' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-request-button">
                    <Plus size={18} />
                    New Request
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Purchase Request</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateRequest} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                        <input
                          type="text"
                          name="full_name"
                          data-testid="request-fullname-input"
                          defaultValue={user.full_name}
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                        <select
                          name="category"
                          data-testid="request-category-select"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        >
                          <option value="">Select category</option>
                          {categoryNames.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Priority</label>
                        <select
                          name="priority"
                          data-testid="request-priority-select"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Needed By</label>
                        <input
                          type="date"
                          name="needed_by"
                          data-testid="request-neededby-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vendor (Optional)</label>
                      <input
                        type="text"
                        name="vendor_name"
                        data-testid="request-vendor-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        placeholder="Enter vendor name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
                      <textarea
                        name="description"
                        data-testid="request-description-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        rows="2"
                        required
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-[#2B3210]">Line Items</label>
                        <button
                          type="button"
                          onClick={addLineItem}
                          data-testid="add-line-item-button"
                          className="text-sm text-[#B2560D] hover:underline"
                        >
                          + Add Item
                        </button>
                      </div>
                      <div className="space-y-3 max-h-64 overflow-y-auto border border-[#E5E2D9] rounded-lg p-4">
                        {lineItems.map((item, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-4">
                              <label className="block text-xs text-[#505631] mb-1">Item Name</label>
                              <input
                                type="text"
                                value={item.item_name}
                                onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                                data-testid={`line-item-name-${index}`}
                                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-[#505631] mb-1">Quantity</label>
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                data-testid={`line-item-quantity-${index}`}
                                min="1"
                                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-[#505631] mb-1">Price (₦)</label>
                              <input
                                type="number"
                                value={item.price}
                                onChange={(e) => updateLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                                data-testid={`line-item-price-${index}`}
                                step="0.01"
                                min="0"
                                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                                required
                              />
                            </div>
                            <div className="col-span-3">
                              <label className="block text-xs text-[#505631] mb-1">Total (₦)</label>
                              <input
                                type="text"
                                value={item.total.toLocaleString()}
                                readOnly
                                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm bg-[#FBF8EF]"
                              />
                            </div>
                            <div className="col-span-1">
                              {lineItems.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeLineItem(index)}
                                  data-testid={`remove-line-item-${index}`}
                                  className="w-full p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <X size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-right">
                        <span className="text-sm font-medium text-[#2B3210]">
                          Grand Total: ₦{calculateGrandTotal().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes/Justification</label>
                      <textarea
                        name="notes"
                        data-testid="request-notes-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        rows="3"
                        placeholder="Explain why these items are needed..."
                        required
                      />
                    </div>

                    <button type="submit" className="btn-primary w-full" data-testid="submit-request-button">
                      Submit Request
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Requestor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E2D9]">
                  {requests.map((request) => (
                    <tr 
                      key={request.id} 
                      data-testid={`request-row-${request.id}`}
                      className={isAdmin ? 'cursor-pointer hover:bg-[#FBF8EF] transition-colors' : ''}
                      onClick={() => isAdmin && handleViewDetails(request)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[#2B3210]">{request.full_name || 'N/A'}</div>
                        <div className="text-sm text-[#505631]">{new Date(request.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E5E2D9] text-[#2B3210]">
                          {request.category || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#2B3210]">{request.description}</div>
                        {request.vendor_name && (
                          <div className="text-sm text-[#505631]">Vendor: {request.vendor_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#2B3210] font-medium">
                        ₦{(request.total_amount || request.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          request.priority === 'high' ? 'bg-red-100 text-red-800' :
                          request.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {request.priority || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`status-badge status-${request.status}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        {isLocked(request.status) && !isAdmin ? (
                          <div className="flex items-center gap-2 text-sm text-[#505631]">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-stone-200 text-stone-700">
                              🔒 Request Locked
                            </span>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-wrap">
                            {/* Edit button - visible for pending requests if user owns it or is admin */}
                            {request.status === 'pending' && (request.requested_by === user.id || isAdmin) && (
                              <button
                                onClick={() => handleEditRequest(request)}
                                data-testid={`edit-request-${request.id}`}
                                className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg transition-colors"
                                title="Edit Request"
                              >
                                <PencilSimple size={18} />
                              </button>
                            )}
                            {isAdmin && request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveRequest(request.id)}
                                  data-testid={`approve-request-${request.id}`}
                                  className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Approve"
                                >
                                  <Check size={18} />
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  data-testid={`reject-request-${request.id}`}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <X size={18} />
                                </button>
                              </>
                            )}
                            {isAdmin && request.status === 'approved' && (
                              <button
                                onClick={() => handleMarkOrdered(request.id)}
                                data-testid={`mark-ordered-${request.id}`}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                Mark Ordered
                              </button>
                            )}
                            {isAdmin && request.status === 'ordered' && (
                              <button
                                onClick={() => handleMarkReceived(request.id)}
                                data-testid={`mark-received-${request.id}`}
                                className="px-3 py-1 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                              >
                                Mark Received
                              </button>
                            )}
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleDeleteRequest(request.id)}
                                  data-testid={`delete-request-${request.id}`}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <X size={18} weight="bold" />
                                </button>
                                <button
                                  onClick={() => handleViewAuditLog(request)}
                                  data-testid={`view-audit-${request.id}`}
                                  className="p-2 text-[#505631] hover:bg-[#E5E2D9] rounded-lg transition-colors"
                                  title="View History"
                                >
                                  <ClockCounterClockwise size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="vendors">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Vendors</h2>
              <Dialog open={openDialog === 'createVendor'} onOpenChange={(open) => setOpenDialog(open ? 'createVendor' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-vendor-button">
                    <Plus size={18} />
                    New Vendor
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Vendor</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateVendor} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vendor Name</label>
                        <input
                          type="text"
                          name="name"
                          data-testid="vendor-name-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                        <select
                          name="category"
                          data-testid="vendor-category-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        >
                          <option value="">Select category</option>
                          <option value="Office Supplies">Office Supplies</option>
                          <option value="Office Supplies & Furniture">Office Supplies & Furniture</option>
                          <option value="Technology & Equipment">Technology & Equipment</option>
                          <option value="Furniture">Furniture</option>
                          <option value="Catering & Food Services">Catering & Food Services</option>
                          <option value="Maintenance & Cleaning">Maintenance & Cleaning</option>
                          <option value="Transportation">Transportation</option>
                          <option value="Professional Services">Professional Services</option>
                          <option value="Construction & Repairs">Construction & Repairs</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Contact Person</label>
                        <input
                          type="text"
                          name="contact_person"
                          data-testid="vendor-contact-person-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
                        <select
                          name="status"
                          data-testid="vendor-status-select"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          defaultValue="active"
                          required
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Email</label>
                        <input
                          type="email"
                          name="email"
                          data-testid="vendor-email-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          data-testid="vendor-phone-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Address</label>
                      <textarea
                        name="address"
                        data-testid="vendor-address-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        rows="2"
                        required
                      />
                    </div>

                    <div className="border-t border-[#E5E2D9] pt-4">
                      <h3 className="text-sm font-semibold text-[#2B3210] mb-3">Payment Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Account Number</label>
                          <input
                            type="text"
                            name="account_number"
                            data-testid="vendor-account-number-input"
                            className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Bank Name</label>
                          <input
                            type="text"
                            name="bank_name"
                            data-testid="vendor-bank-name-input"
                            className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Name on Account</label>
                        <input
                          type="text"
                          name="account_name"
                          data-testid="vendor-account-name-input"
                          className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                          placeholder="Optional"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes</label>
                      <textarea
                        name="notes"
                        data-testid="vendor-notes-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        rows="3"
                        placeholder="Additional notes about this vendor (optional)"
                      />
                    </div>

                    <button type="submit" className="btn-primary w-full" data-testid="submit-vendor-button">
                      Create Vendor
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Contact Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E2D9]">
                  {vendors.map((vendor) => (
                    <tr 
                      key={vendor.id} 
                      data-testid={`vendor-row-${vendor.id}`}
                      className="cursor-pointer hover:bg-[#FBF8EF] transition-colors"
                      onClick={() => handleViewVendor(vendor)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{vendor.name}</td>
                      <td className="px-6 py-4 text-sm text-[#2B3210]">{vendor.contact_person || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#2B3210]">{vendor.email}</div>
                        <div className="text-sm text-[#505631]">{vendor.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E5E2D9] text-[#2B3210]">
                          {vendor.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          vendor.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                            : 'bg-stone-100 text-stone-800 border border-stone-200'
                        }`}>
                          {vendor.status || 'active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="budgets">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Budgets</h2>
              <Dialog open={openDialog === 'budget'} onOpenChange={(open) => setOpenDialog(open ? 'budget' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-budget-button">
                    <Plus size={18} />
                    New Budget
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Budget</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateBudget} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                      <input
                        type="text"
                        name="category"
                        data-testid="budget-category-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Allocated Amount</label>
                      <input
                        type="number"
                        name="allocated_amount"
                        data-testid="budget-amount-input"
                        step="0.01"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Period</label>
                      <select
                        name="period"
                        data-testid="budget-period-select"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      >
                        <option value="">Select period</option>
                        <option value="Q1 2026">Q1 2026</option>
                        <option value="Q2 2026">Q2 2026</option>
                        <option value="Q3 2026">Q3 2026</option>
                        <option value="Q4 2026">Q4 2026</option>
                      </select>
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-budget-button">
                      Create Budget
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Allocated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Usage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {budgets.map((budget) => (
                    <tr 
                      key={budget.id} 
                      data-testid={`budget-row-${budget.id}`}
                      className="cursor-pointer hover:bg-[#FBF8EF] transition-colors"
                      onClick={() => handleViewBudget(budget)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{budget.category}</td>
                      <td className="px-6 py-4 text-sm text-[#2B3210]">
                        ₦{budget.allocated_amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#2B3210]">
                        ₦{budget.spent_amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#505631]">{budget.period}</td>
                      <td className="px-6 py-4">
                        <div className="w-full bg-[#E5E2D9] rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (budget.spent_amount / budget.allocated_amount) * 100 > 90 
                                ? 'bg-red-500' 
                                : (budget.spent_amount / budget.allocated_amount) * 100 > 70 
                                  ? 'bg-amber-500' 
                                  : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min((budget.spent_amount / budget.allocated_amount) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-[#505631] mt-1 block">
                          {((budget.spent_amount / budget.allocated_amount) * 100).toFixed(1)}% used
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9]">
              <h2 className="text-xl font-medium text-[#2B3210]">Manage Categories</h2>
              <p className="text-sm text-[#505631] mt-1">Create, rename, or delete procurement categories</p>
            </div>
            
            {/* Add New Category Form */}
            <div className="p-6 border-b border-[#E5E2D9] bg-[#FBF8EF]">
              <form onSubmit={handleCreateCategory} className="flex gap-3">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter new category name"
                  className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D]"
                  data-testid="new-category-input"
                />
                <button
                  type="submit"
                  disabled={!newCategoryName.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="add-category-btn"
                >
                  <Plus size={18} />
                  Add Category
                </button>
              </form>
            </div>

            {/* Categories List */}
            <div className="divide-y divide-[#E5E2D9]">
              {categories.length === 0 ? (
                <div className="p-8 text-center text-[#505631]">
                  No categories found. Add your first category above.
                </div>
              ) : (
                categories.map((category) => (
                  <div 
                    key={category.id} 
                    className="p-4 flex items-center justify-between hover:bg-[#FBF8EF] transition-colors"
                    data-testid={`category-${category.id}`}
                  >
                    {editingCategory === category.id ? (
                      <div className="flex-1 flex items-center gap-3">
                        <input
                          type="text"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-[#B2560D] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D]"
                          autoFocus
                          data-testid="edit-category-input"
                        />
                        <button
                          onClick={() => handleUpdateCategory(category.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Save"
                          data-testid="save-category-btn"
                        >
                          <Check size={20} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingCategory(null);
                            setEditCategoryName('');
                          }}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="Cancel"
                          data-testid="cancel-edit-btn"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <Folder size={20} className="text-[#B2560D]" />
                          <span className="font-medium text-[#2B3210]">{category.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCategory(category.id);
                              setEditCategoryName(category.name);
                            }}
                            className="p-2 text-[#505631] hover:bg-[#E5E2D9] rounded-lg"
                            title="Rename"
                            data-testid={`edit-category-${category.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Delete"
                            data-testid={`delete-category-${category.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
            
            <div className="p-4 bg-[#FBF8EF] border-t border-[#E5E2D9]">
              <p className="text-sm text-[#505631]">
                <strong>Note:</strong> Categories that are in use by purchase requests, vendors, or budgets cannot be deleted. 
                Renaming a category will update all associated records.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          {analyticsLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B2560D]"></div>
            </div>
          ) : !analyticsData ? (
            <div className="text-center py-20">
              <TrendUp size={48} className="mx-auto text-[#505631] opacity-50 mb-4" />
              <p className="text-[#505631]">Loading analytics data...</p>
              <button onClick={loadAnalytics} className="mt-4 btn-primary">Load Analytics</button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-4">
                  <p className="text-sm text-[#505631]">Total Requests</p>
                  <p className="text-2xl font-bold text-[#2B3210]">{analyticsData.metrics.total_requests}</p>
                </div>
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-4">
                  <p className="text-sm text-[#505631]">Approval Rate</p>
                  <p className="text-2xl font-bold text-emerald-600">{analyticsData.metrics.approval_rate}%</p>
                </div>
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-4">
                  <p className="text-sm text-[#505631]">Avg Request Value</p>
                  <p className="text-2xl font-bold text-[#B2560D]">₦{analyticsData.metrics.avg_request_value?.toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-4">
                  <p className="text-sm text-[#505631]">Pending Requests</p>
                  <p className="text-2xl font-bold text-amber-600">{analyticsData.metrics.pending_count}</p>
                </div>
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Spending by Category */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Spending by Category</h3>
                  {analyticsData.spending_by_category.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.spending_by_category} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                        <XAxis type="number" tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => `₦${value.toLocaleString()}`} />
                        <Bar dataKey="amount" fill="#B2560D" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No spending data available</p>
                  )}
                </div>

                {/* Status Distribution */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Request Status Distribution</h3>
                  {analyticsData.status_distribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.status_distribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="status"
                          label={({ status, percent }) => `${status} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {analyticsData.status_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No status data available</p>
                  )}
                </div>
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Spending Trend */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Monthly Spending Trend</h3>
                  {analyticsData.monthly_trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={analyticsData.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => `₦${value.toLocaleString()}`} />
                        <Area type="monotone" dataKey="amount" stroke="#B2560D" fill="#B2560D" fillOpacity={0.3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No trend data available</p>
                  )}
                </div>

                {/* Spending by Priority */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Spending by Priority</h3>
                  {analyticsData.spending_by_priority.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.spending_by_priority}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                        <XAxis dataKey="priority" />
                        <YAxis tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => `₦${value.toLocaleString()}`} />
                        <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                          {analyticsData.spending_by_priority.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.priority === 'high' ? '#EF4444' : entry.priority === 'medium' ? '#F59E0B' : '#10B981'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No priority data available</p>
                  )}
                </div>
              </div>

              {/* Charts Row 3 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Vendors */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Top Vendors by Spend</h3>
                  {analyticsData.top_vendors.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsData.top_vendors.map((vendor, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-[#B2560D] text-white text-xs flex items-center justify-center font-bold">
                              {index + 1}
                            </span>
                            <span className="text-sm text-[#2B3210]">{vendor.vendor}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#2B3210]">₦{vendor.amount.toLocaleString()}</p>
                            <p className="text-xs text-[#505631]">{vendor.orders} orders</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No vendor data available</p>
                  )}
                </div>

                {/* Budget Utilization */}
                <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                  <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Budget Utilization</h3>
                  {analyticsData.budget_utilization.length > 0 ? (
                    <div className="space-y-4">
                      {analyticsData.budget_utilization.map((budget, index) => (
                        <div key={index}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-[#2B3210]">{budget.category}</span>
                            <span className="text-sm text-[#505631]">
                              ₦{budget.spent.toLocaleString()} / ₦{budget.allocated.toLocaleString()}
                            </span>
                          </div>
                          <div className="w-full bg-[#E5E2D9] rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all ${
                                budget.utilization > 90 ? 'bg-red-500' : 
                                budget.utilization > 70 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(budget.utilization, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-[#505631] mt-1">{budget.utilization}% used • {budget.period}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-[#505631] py-10">No budget data available</p>
                  )}
                </div>
              </div>

              {/* Requests by User */}
              <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
                <h3 className="text-lg font-semibold text-[#2B3210] mb-4">Top Requestors</h3>
                {analyticsData.requests_by_user.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analyticsData.requests_by_user} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="requestor" width={150} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="requests" name="Requests" fill="#505631" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-[#505631] py-10">No requestor data available</p>
                )}
              </div>

              {/* Refresh Button */}
              <div className="flex justify-center">
                <button 
                  onClick={loadAnalytics} 
                  className="btn-secondary flex items-center gap-2"
                  disabled={analyticsLoading}
                >
                  <TrendUp size={18} />
                  {analyticsLoading ? 'Refreshing...' : 'Refresh Analytics'}
                </button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Request Details & Audit Log Dialog */}
      <Dialog open={openDialog === 'view'} onOpenChange={(open) => setOpenDialog(open ? 'view' : null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewMode === 'details' ? 'Request Details' : 'Request History & Audit Trail'}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Summary */}
              <div className="bg-[#FBF8EF] p-6 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-[#2B3210] mb-1">
                      {selectedRequest.description}
                    </h3>
                    <p className="text-sm text-[#505631]">
                      Request ID: {selectedRequest.id}
                    </p>
                  </div>
                  <span className={`status-badge status-${selectedRequest.status} text-sm`}>
                    {selectedRequest.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-[#505631]">Requestor:</span>
                    <p className="text-[#2B3210] font-medium mt-1">
                      {selectedRequest.full_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#505631]">Category:</span>
                    <p className="text-[#2B3210] font-medium mt-1">
                      {selectedRequest.category || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#505631]">Priority:</span>
                    <p className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedRequest.priority === 'high' ? 'bg-red-100 text-red-800' :
                        selectedRequest.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {selectedRequest.priority || 'N/A'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="text-[#505631]">Needed By:</span>
                    <p className="text-[#2B3210] font-medium mt-1">
                      {selectedRequest.needed_by ? new Date(selectedRequest.needed_by).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#505631]">Vendor:</span>
                    <p className="text-[#2B3210] font-medium mt-1">
                      {selectedRequest.vendor_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#505631]">Created:</span>
                    <p className="text-[#2B3210] font-medium mt-1">
                      {new Date(selectedRequest.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              {viewMode === 'details' && selectedRequest.line_items && selectedRequest.line_items.length > 0 && (
                <div>
                  <h3 className="font-medium text-[#2B3210] mb-3">Line Items</h3>
                  <div className="border border-[#E5E2D9] rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#FBF8EF]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Item</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E2D9]">
                        {selectedRequest.line_items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-[#2B3210]">{item.item_name}</td>
                            <td className="px-4 py-3 text-sm text-[#2B3210]">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-[#2B3210]">₦{item.price?.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-[#2B3210] font-medium">₦{item.total?.toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#FBF8EF]">
                          <td colSpan="3" className="px-4 py-3 text-sm font-medium text-[#2B3210] text-right">Grand Total:</td>
                          <td className="px-4 py-3 text-sm font-bold text-[#2B3210]">
                            ₦{(selectedRequest.total_amount || selectedRequest.amount || 0).toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes/Justification */}
              {viewMode === 'details' && selectedRequest.notes && (
                <div>
                  <h3 className="font-medium text-[#2B3210] mb-2">Notes/Justification</h3>
                  <div className="bg-white border border-[#E5E2D9] rounded-lg p-4">
                    <p className="text-sm text-[#505631] whitespace-pre-wrap">{selectedRequest.notes}</p>
                  </div>
                </div>
              )}

              {/* Approval Information */}
              {viewMode === 'details' && selectedRequest.approved_by && (
                <div>
                  <h3 className="font-medium text-[#2B3210] mb-2">Approval Information</h3>
                  <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 text-sm">
                    <p className="text-[#505631]">
                      Approved by: <span className="text-[#2B3210] font-medium">{selectedRequest.approved_by}</span>
                    </p>
                    {selectedRequest.approved_at && (
                      <p className="text-[#505631] mt-1">
                        Date: <span className="text-[#2B3210] font-medium">
                          {new Date(selectedRequest.approved_at).toLocaleString()}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Timeline (for audit mode) */}
              {viewMode === 'audit' && (
                <div>
                  <h3 className="font-medium text-[#2B3210] mb-3">Activity Timeline</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {auditLogs.length === 0 ? (
                      <p className="text-sm text-[#505631] text-center py-4">No activity recorded</p>
                    ) : (
                      auditLogs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className="flex gap-4 p-4 border border-[#E5E2D9] rounded-lg"
                          data-testid={`audit-log-${index}`}
                        >
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-[#B2560D] flex items-center justify-center text-white font-medium">
                              {log.changed_by_name?.charAt(0) || '?'}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-sm font-medium text-[#2B3210]">
                                {log.changed_by_name} {getActionLabel(log.action).toLowerCase()} this request
                              </p>
                              <span className="text-xs text-[#505631]">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-[#505631]">
                              Action: <span className="font-medium">{getActionLabel(log.action)}</span>
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-[#E5E2D9]">
                {viewMode === 'details' && (
                  <button
                    onClick={async () => {
                      await handleViewAuditLog(selectedRequest);
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ClockCounterClockwise size={18} />
                    View History
                  </button>
                )}
                {viewMode === 'audit' && (
                  <button
                    onClick={() => {
                      setViewMode('details');
                    }}
                    className="btn-secondary"
                  >
                    Back to Details
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Details & Purchase History Dialog */}
      <Dialog open={openDialog === 'vendor'} onOpenChange={(open) => setOpenDialog(open ? 'vendor' : null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vendor Details & Purchase History</DialogTitle>
          </DialogHeader>
          {selectedVendor && (
            <div className="space-y-6">
              {/* Vendor Information */}
              <div className="bg-[#FBF8EF] p-6 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-[#2B3210] mb-2">
                      {selectedVendor.name}
                    </h3>
                    <div className="flex gap-2 items-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#E5E2D9] text-[#2B3210]">
                        {selectedVendor.category}
                      </span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedVendor.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-stone-100 text-stone-800 border border-stone-200'
                      }`}>
                        {selectedVendor.status || 'active'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#505631]">Vendor ID</p>
                    <p className="text-xs text-[#505631] font-mono">{selectedVendor.id}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-6 mt-6">
                  <div>
                    <h4 className="text-sm font-semibold text-[#2B3210] mb-3">Contact Information</h4>
                    <div className="space-y-2">
                      {selectedVendor.contact_person && (
                        <div>
                          <span className="text-xs text-[#505631]">Contact Person:</span>
                          <p className="text-sm text-[#2B3210] font-medium">{selectedVendor.contact_person}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-[#505631]">Email:</span>
                        <a href={`mailto:${selectedVendor.email}`} className="block text-sm text-[#B2560D] hover:underline">
                          {selectedVendor.email}
                        </a>
                      </div>
                      <div>
                        <span className="text-xs text-[#505631]">Phone:</span>
                        <a href={`tel:${selectedVendor.phone}`} className="block text-sm text-[#2B3210]">
                          {selectedVendor.phone}
                        </a>
                      </div>
                      <div>
                        <span className="text-xs text-[#505631]">Address:</span>
                        <p className="text-sm text-[#2B3210]">{selectedVendor.address}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-[#2B3210] mb-3">Payment Details</h4>
                    {selectedVendor.account_number || selectedVendor.bank_name ? (
                      <div className="space-y-2">
                        {selectedVendor.bank_name && (
                          <div>
                            <span className="text-xs text-[#505631]">Bank:</span>
                            <p className="text-sm text-[#2B3210] font-medium">{selectedVendor.bank_name}</p>
                          </div>
                        )}
                        {selectedVendor.account_name && (
                          <div>
                            <span className="text-xs text-[#505631]">Account Name:</span>
                            <p className="text-sm text-[#2B3210]">{selectedVendor.account_name}</p>
                          </div>
                        )}
                        {selectedVendor.account_number && (
                          <div>
                            <span className="text-xs text-[#505631]">Account Number:</span>
                            <p className="text-sm text-[#2B3210] font-mono">{selectedVendor.account_number}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#505631] italic">No payment details provided</p>
                    )}
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-[#2B3210] mb-3">Purchase Statistics</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-[#505631]">Total Orders:</span>
                        <span className="text-sm font-medium text-[#2B3210]">{vendorHistory.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#505631]">Total Spent:</span>
                        <span className="text-sm font-medium text-[#2B3210]">
                          ₦{vendorHistory.reduce((sum, req) => sum + (req.total_amount || req.amount || 0), 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#505631]">Approved Orders:</span>
                        <span className="text-sm font-medium text-emerald-700">
                          {vendorHistory.filter(r => r.status === 'approved' || r.status === 'ordered' || r.status === 'received').length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#505631]">Member Since:</span>
                        <span className="text-sm font-medium text-[#2B3210]">
                          {new Date(selectedVendor.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                {selectedVendor.notes && (
                  <div className="mt-4 pt-4 border-t border-[#E5E2D9]">
                    <h4 className="text-sm font-semibold text-[#2B3210] mb-2">Notes</h4>
                    <p className="text-sm text-[#505631] whitespace-pre-wrap">{selectedVendor.notes}</p>
                  </div>
                )}
              </div>

              {/* Purchase History */}
              <div>
                <h3 className="text-lg font-medium text-[#2B3210] mb-4">Purchase History</h3>
                {vendorHistory.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-[#E5E2D9] rounded-lg">
                    <p className="text-[#505631]">No purchase history with this vendor yet</p>
                  </div>
                ) : (
                  <div className="border border-[#E5E2D9] rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#FBF8EF]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Requestor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E2D9] bg-white">
                        {vendorHistory
                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          .map((request) => (
                            <tr 
                              key={request.id}
                              className="hover:bg-[#FBF8EF] cursor-pointer transition-colors"
                              onClick={() => {
                                setOpenDialog(null);
                                setTimeout(() => handleViewDetails(request), 100);
                              }}
                              data-testid={`vendor-history-${request.id}`}
                            >
                              <td className="px-4 py-3 text-sm text-[#505631]">
                                {new Date(request.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-[#2B3210] font-medium">{request.description}</div>
                                {request.category && (
                                  <div className="text-xs text-[#505631] mt-0.5">{request.category}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2B3210]">
                                {request.full_name || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2B3210] font-medium">
                                ₦{(request.total_amount || request.amount || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`status-badge status-${request.status} text-xs`}>
                                  {request.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Request Dialog */}
      <Dialog open={openDialog === 'editRequest'} onOpenChange={(open) => {
        setOpenDialog(open ? 'editRequest' : null);
        if (!open) {
          setEditingRequest(null);
          setEditLineItems([]);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Purchase Request</DialogTitle>
          </DialogHeader>
          {editingRequest && (
            <form onSubmit={handleUpdateRequest} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    data-testid="edit-full-name-input"
                    defaultValue={editingRequest.full_name}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                  <select
                    name="category"
                    data-testid="edit-category-select"
                    defaultValue={editingRequest.category}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                    required
                  >
                    {categoryNames.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Priority</label>
                  <select
                    name="priority"
                    data-testid="edit-priority-select"
                    defaultValue={editingRequest.priority}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vendor (Optional)</label>
                  <select
                    name="vendor_name"
                    data-testid="edit-vendor-select"
                    defaultValue={editingRequest.vendor_name || ''}
                    className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  >
                    <option value="">Select vendor (optional)</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.name}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Needed By</label>
                <input
                  type="date"
                  name="needed_by"
                  data-testid="edit-needed-by-input"
                  defaultValue={editingRequest.needed_by}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
                <textarea
                  name="description"
                  data-testid="edit-description-input"
                  defaultValue={editingRequest.description}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  rows="2"
                  required
                />
              </div>

              {/* Line Items Section */}
              <div className="border border-[#E5E2D9] rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-[#2B3210]">Line Items</h3>
                  <button
                    type="button"
                    onClick={addEditLineItem}
                    className="text-sm text-[#B2560D] hover:underline flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>
                <div className="space-y-3">
                  {editLineItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5">
                        <input
                          type="text"
                          placeholder="Item name"
                          value={item.item_name}
                          onChange={(e) => updateEditLineItem(index, 'item_name', e.target.value)}
                          data-testid={`edit-line-item-name-${index}`}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          placeholder="Qty"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateEditLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          data-testid={`edit-line-item-qty-${index}`}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          placeholder="Price"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateEditLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                          data-testid={`edit-line-item-price-${index}`}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="px-3 py-2 bg-[#FBF8EF] rounded-lg text-sm font-medium text-[#2B3210]">
                          ₦{item.total.toLocaleString()}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeEditLineItem(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-[#E5E2D9] flex justify-end">
                  <div className="text-lg font-semibold text-[#2B3210]">
                    Total: <span className="text-[#B2560D]">₦{calculateEditGrandTotal().toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  data-testid="edit-notes-input"
                  defaultValue={editingRequest.notes}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:ring-2 focus:ring-[#B2560D]/20 focus:border-[#B2560D]"
                  rows="2"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setOpenDialog(null);
                    setEditingRequest(null);
                    setEditLineItems([]);
                  }}
                  className="flex-1 px-4 py-2.5 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-edit-request-button"
                  className="flex-1 btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Budget Details Dialog */}
      <Dialog open={openDialog === 'budgetDetails'} onOpenChange={(open) => {
        setOpenDialog(open ? 'budgetDetails' : null);
        if (!open) setSelectedBudget(null);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Budget Details</DialogTitle>
          </DialogHeader>
          {selectedBudget && (
            <div className="space-y-6">
              {/* Budget Overview Card */}
              <div className="bg-[#FBF8EF] p-6 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-[#2B3210] mb-1">
                      {selectedBudget.category}
                    </h3>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#E5E2D9] text-[#2B3210]">
                      {selectedBudget.period}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#505631]">Budget ID</p>
                    <p className="text-xs text-[#505631] font-mono">{selectedBudget.id?.slice(0, 8)}...</p>
                  </div>
                </div>

                {/* Budget Progress */}
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-[#2B3210]">Budget Usage</span>
                    <span className={`text-sm font-bold ${
                      (selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 90 
                        ? 'text-red-600' 
                        : (selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 70 
                          ? 'text-amber-600' 
                          : 'text-emerald-600'
                    }`}>
                      {((selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-[#E5E2D9] rounded-full h-4">
                    <div 
                      className={`h-4 rounded-full transition-all ${
                        (selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 90 
                          ? 'bg-red-500' 
                          : (selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 70 
                            ? 'bg-amber-500' 
                            : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min((selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 text-center">
                  <p className="text-sm text-[#505631] mb-1">Allocated</p>
                  <p className="text-xl font-bold text-[#2B3210]">
                    ₦{selectedBudget.allocated_amount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 text-center">
                  <p className="text-sm text-[#505631] mb-1">Spent</p>
                  <p className="text-xl font-bold text-[#B2560D]">
                    ₦{selectedBudget.spent_amount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 text-center">
                  <p className="text-sm text-[#505631] mb-1">Remaining</p>
                  <p className={`text-xl font-bold ${
                    (selectedBudget.allocated_amount - selectedBudget.spent_amount) < 0 
                      ? 'text-red-600' 
                      : 'text-emerald-600'
                  }`}>
                    ₦{(selectedBudget.allocated_amount - selectedBudget.spent_amount).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Budget Status Alert */}
              {(selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 90 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <span className="text-red-600 font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-800">Budget Alert</p>
                    <p className="text-sm text-red-700">
                      This budget has exceeded 90% usage. Consider reviewing spending or requesting additional funds.
                    </p>
                  </div>
                </div>
              )}
              {(selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 > 70 && 
               (selectedBudget.spent_amount / selectedBudget.allocated_amount) * 100 <= 90 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span className="text-amber-600 font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Budget Warning</p>
                    <p className="text-sm text-amber-700">
                      This budget has exceeded 70% usage. Monitor spending closely.
                    </p>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="border-t border-[#E5E2D9] pt-4">
                <p className="text-sm text-[#505631]">
                  Created: <span className="text-[#2B3210] font-medium">
                    {selectedBudget.created_at ? new Date(selectedBudget.created_at).toLocaleString() : 'N/A'}
                  </span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Standalone Create Request Dialog - Can be triggered from Quick Actions */}
      <Dialog open={openDialog === 'request'} onOpenChange={(open) => setOpenDialog(open ? 'request' : null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRequest} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  defaultValue={user.full_name}
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                <select
                  name="category"
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                  required
                >
                  <option value="">Select category</option>
                  {categoryNames.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Priority</label>
                <select
                  name="priority"
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                  required
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Needed By</label>
                <input
                  type="date"
                  name="needed_by"
                  className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vendor (Optional)</label>
              <input
                type="text"
                name="vendor_name"
                className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                placeholder="Enter vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <textarea
                name="description"
                rows={3}
                className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                placeholder="Describe what you need to purchase"
                required
              />
            </div>

            {/* Line Items */}
            <div className="border border-[#E5E2D9] rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-[#2B3210]">Line Items</label>
                <button
                  type="button"
                  onClick={() => setLineItems([...lineItems, { item_name: '', quantity: 1, price: 0, total: 0 }])}
                  className="text-sm text-[#B2560D] hover:underline"
                >
                  + Add Item
                </button>
              </div>
              
              <div className="space-y-3">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-5">
                      <input
                        type="text"
                        placeholder="Item name"
                        value={item.item_name}
                        onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => updateLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2 text-right font-medium text-[#2B3210] text-sm">
                      ₦{item.total.toLocaleString()}
                    </div>
                    <div className="col-span-1">
                      {lineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t border-[#E5E2D9] text-right">
                <span className="text-sm text-[#505631]">Total: </span>
                <span className="text-lg font-bold text-[#B2560D]">
                  ₦{lineItems.reduce((sum, item) => sum + item.total, 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes (Optional)</label>
              <textarea
                name="notes"
                rows={2}
                className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                placeholder="Any additional notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setOpenDialog(null)}
                className="px-4 py-2 border border-[#E5E2D9] rounded-lg text-[#505631]"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Submit Request
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Guide Dialog */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-[#2B3210]">
              <Book size={24} className="text-[#B2560D]" />
              Procurement Module Guide
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Introduction */}
            <div className="bg-[#FBF8EF] rounded-lg p-4 border border-[#E5E2D9]">
              <p className="text-sm text-[#505631]">
                Welcome to the Procurement module! This guide will help you understand how to 
                {isAdmin ? ' manage all procurement operations including approvals, vendors, and budgets.' : ' submit and track your purchase requests.'}
              </p>
            </div>

            {/* Common User Guide */}
            <div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3 flex items-center gap-2">
                <ShoppingCart size={20} className="text-[#B2560D]" />
                Creating Purchase Requests
              </h3>
              <div className="space-y-2 text-sm text-[#505631]">
                <div className="flex gap-2">
                  <span className="font-medium text-[#2B3210] min-w-[24px]">1.</span>
                  <span>Click the <strong>"New Request"</strong> button or use Quick Actions on the Dashboard.</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-[#2B3210] min-w-[24px]">2.</span>
                  <span>Fill in the required details: your name, category, priority, and needed-by date.</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-[#2B3210] min-w-[24px]">3.</span>
                  <span>Add line items with item name, quantity, and price. The total is calculated automatically.</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-[#2B3210] min-w-[24px]">4.</span>
                  <span>Provide a justification in the Notes field explaining why the items are needed.</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-[#2B3210] min-w-[24px]">5.</span>
                  <span>Click <strong>"Submit Request"</strong> to send for approval.</span>
                </div>
              </div>
            </div>

            {/* Request Status Guide */}
            <div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Understanding Request Status</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <div>
                    <span className="font-medium text-amber-700">Pending</span>
                    <p className="text-xs text-amber-600">Awaiting admin approval</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <div>
                    <span className="font-medium text-emerald-700">Approved</span>
                    <p className="text-xs text-emerald-600">Ready to be ordered</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <div>
                    <span className="font-medium text-blue-700">Ordered</span>
                    <p className="text-xs text-blue-600">Order placed with vendor</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <div>
                    <span className="font-medium text-purple-700">Received</span>
                    <p className="text-xs text-purple-600">Items delivered</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200 sm:col-span-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div>
                    <span className="font-medium text-red-700">Rejected</span>
                    <p className="text-xs text-red-600">Request was declined - check notes for reason</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Editing Requests */}
            <div>
              <h3 className="text-lg font-semibold text-[#2B3210] mb-3">Editing Your Requests</h3>
              <p className="text-sm text-[#505631]">
                You can only edit requests while they are in <strong>Pending</strong> status. 
                Once a request is approved, rejected, or processed further, it becomes locked.
                Click the <strong>pencil icon</strong> on a pending request to make changes.
              </p>
            </div>

            {/* Admin-Only Section */}
            {isAdmin && (
              <>
                <div className="border-t border-[#E5E2D9] pt-6">
                  <h3 className="text-lg font-semibold text-[#B2560D] mb-3 flex items-center gap-2">
                    <Warning size={20} />
                    Administrator Functions
                  </h3>
                </div>

                {/* Approving Requests */}
                <div>
                  <h4 className="font-medium text-[#2B3210] mb-2 flex items-center gap-2">
                    <Check size={18} className="text-emerald-600" />
                    Approving & Rejecting Requests
                  </h4>
                  <div className="space-y-2 text-sm text-[#505631] pl-6">
                    <p>• Review pending requests in the <strong>Purchase Requests</strong> tab.</p>
                    <p>• Click the <strong>green checkmark</strong> to approve or <strong>red X</strong> to reject.</p>
                    <p>• Overdue requests (pending &gt;3 days) appear in the Dashboard alert section.</p>
                    <p>• After approval, use <strong>"Mark Ordered"</strong> when the order is placed.</p>
                    <p>• Use <strong>"Mark Received"</strong> when items are delivered.</p>
                  </div>
                </div>

                {/* Managing Vendors */}
                <div>
                  <h4 className="font-medium text-[#2B3210] mb-2 flex items-center gap-2">
                    <Storefront size={18} className="text-emerald-600" />
                    Managing Vendors
                  </h4>
                  <div className="space-y-2 text-sm text-[#505631] pl-6">
                    <p>• Access the <strong>Vendors</strong> tab to view and manage suppliers.</p>
                    <p>• Add new vendors with contact details and payment information.</p>
                    <p>• Click on a vendor row to view their purchase history.</p>
                    <p>• Set vendor status to Active or Inactive as needed.</p>
                  </div>
                </div>

                {/* Managing Budgets */}
                <div>
                  <h4 className="font-medium text-[#2B3210] mb-2 flex items-center gap-2">
                    <Wallet size={18} className="text-blue-600" />
                    Managing Budgets
                  </h4>
                  <div className="space-y-2 text-sm text-[#505631] pl-6">
                    <p>• Use the <strong>Budgets</strong> tab to set spending limits by category.</p>
                    <p>• Define allocated amounts and periods (e.g., Q1 2026).</p>
                    <p>• Monitor spending vs. allocation to stay within budget.</p>
                    <p>• Click a budget to view detailed spending breakdown.</p>
                  </div>
                </div>

                {/* Managing Categories */}
                <div>
                  <h4 className="font-medium text-[#2B3210] mb-2 flex items-center gap-2">
                    <Folder size={18} className="text-amber-600" />
                    Managing Categories
                  </h4>
                  <div className="space-y-2 text-sm text-[#505631] pl-6">
                    <p>• Access the <strong>Categories</strong> tab to customize procurement categories.</p>
                    <p>• Add new categories to organize purchase requests.</p>
                    <p>• Edit category names by clicking the pencil icon.</p>
                    <p>• Delete unused categories (be cautious - this cannot be undone).</p>
                  </div>
                </div>

                {/* Analytics */}
                <div>
                  <h4 className="font-medium text-[#2B3210] mb-2 flex items-center gap-2">
                    <TrendUp size={18} className="text-purple-600" />
                    Analytics & Reports
                  </h4>
                  <div className="space-y-2 text-sm text-[#505631] pl-6">
                    <p>• View the <strong>Analytics</strong> tab for spending insights and trends.</p>
                    <p>• Analyze spending by category, vendor, and time period.</p>
                    <p>• Use <strong>Export Report</strong> to download procurement data as CSV.</p>
                  </div>
                </div>
              </>
            )}

            {/* Tips Section */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">💡 Tips</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Always provide detailed justification to speed up approvals.</li>
                <li>• Set realistic "Needed By" dates to help prioritize requests.</li>
                <li>• Use High priority only for urgent, time-sensitive purchases.</li>
                {isAdmin && <li>• Review pending requests daily to avoid bottlenecks.</li>}
              </ul>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-[#E5E2D9]">
            <button
              onClick={() => setShowGuide(false)}
              className="px-6 py-2 bg-[#B2560D] text-white rounded-lg hover:bg-[#8B4513] transition-colors"
              data-testid="close-guide-button"
            >
              Got it!
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Procurement;
